// Components
import Table from '../../../atoms/Table/Table';
import Checkbox from '../../../atoms/Checkbox/Checkbox';
import Picto from '../../../atoms/Picto/Picto';
import MarkdownContainer from '../../../atoms/MarkdownContainer/MarkdownContainer';

// Hooks
import { useTableFieldSelection } from '../../../../domain/tableFieldSelection/useTableFieldSelection';

// Utils
import cn from '../../../../utils/cn';

// Styles
import styles from './TableField.module.css';

/**
 * TableField — interactive table for form cell selection.
 * One cell must be selected per row (radio-like). Column headers allow selecting an entire column.
 * @param {object} props
 * @param {FormTableData} props.tableData - Table configuration (headers, groups, footers)
 * @param {FormTableSelection[]} props.value - Currently selected cells
 * @param {(selections: FormTableSelection[]) => void} props.onChange - Selection change callback
 * @param {boolean} [props.isReadOnly] - Locked; value remains in form
 * @returns {JSX.Element|null}
 */
function TableField({
  tableData, value = [], onChange, isReadOnly = false,
}) {
  if (!tableData) return null;

  const { headers, groups, footers } = tableData;

  const {
    isSelected, isBaseColumn, handleCellClick, handleColumnSelect,
  } = useTableFieldSelection({ tableData, value, onChange });

  const colCount = headers.length;

  return (
    <div className={styles['table-field-wrapper']}>
      <Table width="100%">
        <>
          {headers.length > 0 && (
            <thead>
              <tr>
                {headers.map((header) => (
                  <th
                    key={header.colId}
                    className={styles['table-header-cell']}
                    style={{
                      width: header.width || 'auto',
                      textAlign: /** @type {'left'|'center'|'right'} */ (header.align || 'center'),
                    }}
                    title={header.value}
                  >
                    <div className={styles['table-header-cell-content']}>
                      {header.selectable !== false && (
                        <Checkbox
                          id={`table-col-${header.colId}`}
                          checked={isBaseColumn(header.colId)}
                          onChange={() => { if (!isReadOnly) handleColumnSelect(header.colId); }}
                          isReadOnly={isReadOnly}
                          size="small"
                          theme="secondary"
                        />
                      )}
                      <MarkdownContainer content={header.value} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
          )}

          {groups.map((group) => (
            <tbody key={group.lib}>
              {group.lib && (
                <tr>
                  <td
                    className={styles['table-group-header']}
                    colSpan={colCount}
                  >
                    {group.lib}
                  </td>
                </tr>
              )}

              {group.rows.map((row) => {
                const firstCell = row.cells.find((c) => c.text != null);
                const rowId = firstCell?.text?.rowId;
                if (!rowId) return null;

                return (
                  <tr key={rowId}>
                    {headers.map((header) => {
                      const cell = row.cells.find((c) => c.text?.colId === header.colId);
                      if (!cell?.text) return null;
                      const { colId } = cell.text;
                      const selectable = header.selectable !== false;
                      const selected = selectable && isSelected(rowId, colId);

                      return (
                        <td
                          key={`${rowId}-${colId}`}
                          className={cn([
                            styles['table-body-cell'],
                            selected && styles['table-body-cell-selected'],
                            !selectable && styles['table-body-cell-not-selectable'],
                          ])}
                          style={{
                            textAlign: /** @type {'left'|'center'|'right'} */ (
                              header.align || 'left'
                            ),
                          }}
                          onClick={selectable && !isReadOnly
                            ? () => handleCellClick(rowId, colId)
                            : undefined}
                        >
                          <div className={styles['table-body-cell-content']}>
                            {cell.text.value}
                            {
                            selected && (
                              <Picto icon="circle-check" className={styles['selected-icon']} />
                            )
                          }
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          ))}

          {footers && footers.length > 0 && (
            <tfoot>
              <tr>
                {footers.map((footer) => (
                  <td
                    key={`footer-${footer.value}-${footer.align || 'left'}`}
                    className={styles['table-footer-cell']}
                    style={{
                      textAlign: /** @type {'left'|'center'|'right'} */ (footer.align || 'left'),
                    }}
                  >
                    <MarkdownContainer content={footer.value} />
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </>
      </Table>
    </div>
  );
}

export default TableField;
